import React from 'react';
import { Card, Button, Icon } from '../../components/ui';

/*
 * EntityEditor — the right pane of the Setup master-detail workspace.
 * Presentational shell only: crumbs + title + status pill header, a scrollable
 * body (the entity's fields, supplied by children), and a footer with
 * Save / Cancel / Delete. Save/Delete loading + disabled states are driven by
 * props; the actual CRUD is done by the entity component that renders this.
 *
 * Props:
 *   crumbs      : array of strings (Customer › Site › …) — optional
 *   title       : editor heading (e.g. gate name / "New Gate")
 *   statusPill  : optional { label, variant } rendered right of the title
 *   onSubmit    : form submit handler (create/update) — REQUIRED for the form
 *   onCancel    : cancel handler (reset selection) — optional
 *   onDelete    : delete handler — omit to hide the Delete button (e.g. create)
 *   saving      : bool — Save button spinner + disabled
 *   deleting    : bool — Delete button spinner + disabled
 *   saveLabel   : Save button text (default "Save changes")
 *   deleteLabel : Delete button text (default "Delete")
 *   canSave     : bool — enable/disable Save (default true)
 *   children    : the entity's fields
 */
export default function EntityEditor({
  crumbs,
  title,
  statusPill,
  onSubmit,
  onCancel,
  onDelete,
  saving = false,
  deleting = false,
  saveLabel = 'Save changes',
  deleteLabel = 'Delete',
  canSave = true,
  readOnly = false,
  chromeless = false,
  children,
}) {
  // Standard form-footer order: destructive action far-left (separated so it's not next to the
  // commit button), primary action far-right with Cancel to its left.
  const footer = (
    <div className="editor__foot">
      {onDelete && (
        <Button variant="danger" icon={<Icon name="trash" size={15} />} onClick={onDelete} loading={deleting} disabled={saving}>
          {deleteLabel}
        </Button>
      )}
      <div className="grow" />
      {onCancel && (
        <Button variant="default" onClick={onCancel} disabled={saving || deleting}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
      )}
      {!readOnly && (
        <Button variant="primary" onClick={onSubmit} loading={saving} disabled={!canSave}>
          {saveLabel}
        </Button>
      )}
    </div>
  );
  const form = (
    <form className="editor__body" onSubmit={(e) => { e.preventDefault(); if (!readOnly && canSave && !saving) onSubmit(e); }}>
      {children}
      {/* hidden submit so Enter submits the native form */}
      <button type="submit" style={{ display: 'none' }} aria-hidden="true" tabIndex={-1} />
    </form>
  );

  // chromeless: no Card/header — the unified pane's tab shell already shows the crumbs/title, so the
  // editor renders just its fields + the Save/Cancel/Delete footer, filling the tab body.
  if (chromeless) {
    return <div className="editor editor--chromeless">{form}{footer}</div>;
  }

  return (
    <div className="editor">
      <Card
        flush
        header={
          <div className="editor__head">
            {Array.isArray(crumbs) && crumbs.filter(Boolean).length > 0 && (
              <div className="crumbs">
                {crumbs.filter(Boolean).map((c, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <span className="sepc">›</span>}
                    <b>{c}</b>
                  </React.Fragment>
                ))}
              </div>
            )}
            <div className="spread">
              <h2 className="h-page truncate">{title}</h2>
              {statusPill && (
                <span className={`pill pill--${statusPill.variant || 'plain'}`}>{statusPill.label}</span>
              )}
            </div>
          </div>
        }
        footer={footer}
      >
        {form}
      </Card>
    </div>
  );
}

/*
 * EmptyEditor — shown when nothing is selected in the left pane.
 */
export function EmptyEditor({ icon = 'settings', title = 'Nothing selected', message }) {
  return (
    <div className="editor">
      <Card flush>
        <div className="setup-empty">
          <Icon name={icon} size={44} strokeWidth={1.4} />
          <div>
            <strong>{title}</strong>
            {message && <div className="sub" style={{ marginTop: 4 }}>{message}</div>}
          </div>
        </div>
      </Card>
    </div>
  );
}
